import { useEffect, useState } from 'react';
import { authService, type AuthConfig } from '@/services/auth.service';
import { BLOCKED_UPLOAD_EXTENSIONS } from '@/services/file.service';
import { DEFAULT_UPLOAD_RESUME_WINDOW_MS } from '@/services/direct-upload.service';

/**
 * GET /auth/config, fetched once per page load and shared by every hook that
 * needs a deployment-level rule (password policy, upload blocklist). The
 * request is public, so it works before login too. Until it answers — or if
 * it fails — the backend defaults below apply, so nothing renders empty.
 */
const FALLBACK: AuthConfig = {
  saml_enabled: false,
  password_min_length: 12,
  password_max_age_days: 90,
  upload_blocked_extensions: [...BLOCKED_UPLOAD_EXTENSIONS],
  upload_resume_window_seconds: DEFAULT_UPLOAD_RESUME_WINDOW_MS / 1000,
};

let cached: AuthConfig | null = null;
let inflight: Promise<AuthConfig> | null = null;

function load(): Promise<AuthConfig> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = authService
      .getAuthConfig()
      .then((cfg) => {
        cached = { ...FALLBACK, ...cfg };
        return cached;
      })
      .catch(() => FALLBACK)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useAuthConfig(): AuthConfig {
  const [config, setConfig] = useState<AuthConfig>(cached ?? FALLBACK);
  useEffect(() => {
    let cancelled = false;
    load().then((c) => {
      if (!cancelled) setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return config;
}

/**
 * The upload blocklist the API enforces (QA #74), lower-cased extensions
 * without dots, and the resume window (ADR-054) in ms — how long a chunked
 * upload waits out a network outage; 0 = not at all.
 */
export function useUploadPolicy(): { blockedExtensions: string[]; resumeWindowMs: number } {
  const cfg = useAuthConfig();
  const seconds = cfg.upload_resume_window_seconds;
  return {
    blockedExtensions: cfg.upload_blocked_extensions ?? FALLBACK.upload_blocked_extensions ?? [],
    resumeWindowMs:
      typeof seconds === 'number' && seconds >= 0 ? seconds * 1000 : DEFAULT_UPLOAD_RESUME_WINDOW_MS,
  };
}

/**
 * The e-mail domains the API accepts (QA #91 user accounts, QA #94 alert
 * recipients); an empty list means any domain.
 */
export function useEmailDomainPolicy(): { userDomains: string[]; alertDomains: string[] } {
  const cfg = useAuthConfig();
  return {
    userDomains: cfg.allowed_user_email_domains ?? [],
    alertDomains: cfg.allowed_alert_email_domains ?? [],
  };
}
